import { useCallback, useContext, useEffect, useRef } from 'react'
import useFetch, { CachePolicies } from 'use-http'
import { isUndefined } from 'lodash'
import { FeaturesEnum } from '../../utils/features'
import type { MediaLibrarySearchResponse, MLSort } from '../interfaces'
import { UserContext } from '../providers'
import { PublicLinksInfoContext } from '../providers/PublicLinksInfoProvider'
import type { FileType } from '../constants'
import { buildSearchQuery } from '../../utils/search'

interface Data<T> {
  success: boolean
  items: T[]
  error: unknown
}

export interface UseMediaLibrarySearchProps<T> {
  path: string | null
  skip: number
  collectionId?: true | string
  isDrawerSearch?: boolean
  limit: number
  sort: MLSort
  q: string
  searchMode: string | null
  searchInputMode: string
  searchByTags?: boolean
  searchQuery: string
  semantic?: string
  semanticUrl?: string
  fileType?: FileType | ""
  minScore?: number
  semanticSupported?: boolean
  suppressRequest?: boolean
  assetType?: 'file' | 'folder' | 'media-collection' | null
  isVersionIncludedInSearch?: boolean
  onData(data: Data<T>): void
  onLoading(value: boolean): void
}

function useMediaLibrarySearch<T = MediaLibrarySearchResponse>({
  path = '/',
  skip,
  collectionId,
  isDrawerSearch,
  limit,
  sort,
  q,
  searchQuery,
  searchMode,
  searchInputMode,
  searchByTags,
  semantic,
  semanticUrl,
  fileType,
  semanticSupported = false,
  minScore,
  assetType = null,
  isVersionIncludedInSearch = false,
  onData,
  onLoading,
  suppressRequest = false,
}: UseMediaLibrarySearchProps<T>): {
  loading: boolean
  refetch(options?: Partial<UseMediaLibrarySearchProps<T>>): Promise<void>
  abort(): void
} {
  const { isPublicLink, urlPrefix } = useContext(PublicLinksInfoContext)
  const { user } = useContext(UserContext)
  const skipToUse = useRef<Number>(0)

  // tracks "latest started request"
  const latestRequestIdRef = useRef(0)

  // Track the request ID that's currently loading (the latest one)
  const loadingRequestIdRef = useRef<number | null>(null)

  const generateUrl = useCallback(
    (overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {}): string => {
      skipToUse.current = isUndefined(overrideParams.skip)
        ? skip
        : overrideParams.skip

      const params: Record<string, string> = {
        skip: skipToUse.current.toString(),
        limit: (isUndefined(overrideParams.limit)
          ? limit
          : overrideParams.limit
        ).toString(),
        sort: isUndefined(overrideParams.sort) ? sort : overrideParams.sort,
      }

      // Get final values (considering overrides)
      const finalQ = isUndefined(overrideParams.q) ? q : overrideParams.q
      const finalSearchMode = isUndefined(overrideParams.searchMode)
        ? searchMode
        : overrideParams.searchMode
      const finalSearchInputMode = isUndefined(overrideParams.searchInputMode)
        ? searchInputMode
        : overrideParams.searchInputMode
      const finalMinScore = isUndefined(overrideParams.minScore)
        ? minScore
        : overrideParams.minScore
      const finalPath = isUndefined(overrideParams.path)
        ? path
        : overrideParams.path
      const finalFileType = isUndefined(overrideParams.fileType)
        ? fileType
        : overrideParams.fileType
      const finalSearchQuery = isUndefined(overrideParams.searchQuery)
        ? searchQuery
        : overrideParams.searchQuery
      const finalAssetType = isUndefined(overrideParams.assetType)
        ? assetType
        : overrideParams.assetType
      const finalIsVersionIncludedInSearch = isUndefined(
        overrideParams.isVersionIncludedInSearch,
      )
        ? isVersionIncludedInSearch
        : overrideParams.isVersionIncludedInSearch
      const finalSearchByTags = isUndefined(overrideParams.searchByTags)
        ? searchByTags
        : overrideParams.searchByTags

      // Handle AI modes first (semantic/semanticUrl)
      if (finalQ && finalSearchMode === 'ai-text') {
        params.semantic = finalQ
      } else if (finalQ && finalSearchMode === 'ai-image') {
        params.semanticUrl = finalQ
      }

      if (finalPath !== null) {
        params.path = finalPath
      }

      if (!isUndefined(finalMinScore)) {
        params.minScore = finalMinScore.toString()
      }

      // Use buildSearchQuery to build the final search query
      // This handles: assetType, isVersionIncludedInSearch, q, searchMode,
      // searchInputMode, searchByTags, searchQuery, fileType
      const finalBuiltSearchQuery = buildSearchQuery({
        assetType: finalAssetType,
        isVersionIncludedInSearch: finalIsVersionIncludedInSearch,
        q: finalQ,
        searchMode: finalSearchMode,
        searchInputMode: finalSearchInputMode as 'builder' | 'query',
        searchByTags: finalSearchByTags,
        searchQuery: finalSearchQuery, // Base searchQuery (filters)
        fileType: finalFileType ?? '',
      })

      if (finalBuiltSearchQuery) {
        params.searchQuery = finalBuiltSearchQuery
      }

      const searchParams = new URLSearchParams(params)

      return `?${searchParams.toString()}`
    },
    [
      q,
      path,
      skip,
      limit,
      sort,
      searchQuery,
      searchMode,
      searchInputMode,
      searchByTags,
      minScore,
      fileType,
      assetType,
      isVersionIncludedInSearch,
    ],
  )

  const constructEndpoint = useCallback(
    (overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {}): string => {
      let endpoint = ''

      const collectionIdParam =
        overrideParams.collectionId !== undefined
          ? overrideParams.collectionId
          : collectionId

      if (collectionIdParam === true) {
        endpoint = `${urlPrefix}api/v2/media-collections${generateUrl(
          overrideParams,
        )}`
      } else if (typeof collectionIdParam === 'string') {
        endpoint = `${urlPrefix}api/v2/media-collections/${collectionIdParam}/assets${generateUrl(
          overrideParams,
        )}`
      } else {
        endpoint = `${urlPrefix}api/v2/files${generateUrl(overrideParams)}`
      }

      return endpoint
    },
    [urlPrefix, collectionId, generateUrl],
  )

  const { loading, get, data, abort, error } = useFetch<Data<T>>({
    interceptors: {
      request: ({ options, path: requestPath }) => {
        if (
          requestPath?.startsWith(`${urlPrefix}api/v2/media-collections`) &&
          !isPublicLink &&
          !user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          )
        ) {
          const abortController = new AbortController()
          abortController.abort()

          return {
            ...options,
            signal: abortController.signal,
          }
        }
        return options
      },
    },
    cachePolicy: CachePolicies.NO_CACHE,
    onNewData: (currentData: Data<T> | undefined, newData: T[] | any) => {
      let merged: Data<T>
      if (skipToUse.current === 0) {
        if (!Array.isArray(newData)) {
          merged = {
            success: false,
            error: newData,
            items: [] as unknown as T[],
          }
        } else {
          merged = { success: true, error: null, items: newData }
        }
      } else {
        const nextItems = Array.isArray(newData) ? newData : []
        merged = {
          success: true,
          error: null,
          items: [...(currentData?.items ?? []), ...nextItems],
        }
      }
      return merged
    },
  })

  // all requests go through this wrapper; only the latest ID wins.
  const runRequest = useCallback(
    (
      overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {},
    ): Promise<void> => {
      if (suppressRequest) {
        // If request is suppressed, ensure loading is false
        if (loadingRequestIdRef.current !== null) {
          loadingRequestIdRef.current = null
          onLoading(false)
        }
        return Promise.resolve()
      }

      const endpoint = constructEndpoint(overrideParams)

      const myRequestId = ++latestRequestIdRef.current

      // Mark this as the currently loading request
      loadingRequestIdRef.current = myRequestId

      // Set loading to true immediately for the latest request
      onLoading(true)

      return get(endpoint)
        .then((result: Data<T> | undefined) => {
          // Ignore if this is not the latest started request.
          if (myRequestId !== latestRequestIdRef.current) {
            return
          }

          const finalData: Data<T> =
            result ??
            ({ success: false, error: null, items: [] as T[] } as Data<T>)

          onData(finalData)

          // Only set loading to false if this is still the latest request
          if (
            myRequestId === latestRequestIdRef.current &&
            loadingRequestIdRef.current === myRequestId
          ) {
            loadingRequestIdRef.current = null
            onLoading(false)
          }
        })
        .catch(err => {
          // Handle errors - only update loading if this is still the latest request
          if (
            myRequestId === latestRequestIdRef.current &&
            loadingRequestIdRef.current === myRequestId
          ) {
            loadingRequestIdRef.current = null
            onLoading(false)
          }
          // Re-throw error so caller can handle it if needed
          throw err
        })
    },
    [constructEndpoint, get, suppressRequest],
  )

  useEffect(() => {
    if (suppressRequest) {
      return
    }

    if (isPublicLink && isDrawerSearch) {
      return
    }
    if ((semantic || semanticUrl) && !semanticSupported && !isDrawerSearch) {
      // Mark this as the latest logical "result" so any in-flight requests are treated as stale
      latestRequestIdRef.current++
      // Clear loading state since we're not making a request
      loadingRequestIdRef.current = null
      onLoading(false)
      onData({ success: true, error: null, items: [] as T[] })
      return
    }

    void runRequest()
  }, [
    path,
    skip,
    limit,
    sort,
    q,
    fileType,
    searchQuery,
    searchMode,
    searchInputMode,
    searchByTags,
    collectionId,
    isDrawerSearch,
    semantic,
    semanticUrl,
    semanticSupported,
    suppressRequest,
    assetType,
    isVersionIncludedInSearch,
    isPublicLink,
    runRequest,
  ])

  // Safety net: Use useFetch's loading state as a fallback
  // Only sync when useFetch loading becomes false AND we have no tracked request
  // This is a last resort to prevent loading from getting stuck
  const prevLoadingRef = useRef(loading)
  useEffect(() => {
    // Only act when loading transitions from true to false
    if (
      prevLoadingRef.current &&
      !loading &&
      loadingRequestIdRef.current === null
    ) {
      onLoading(false)
    }
    prevLoadingRef.current = loading
  }, [loading])

  // Cleanup: If suppressRequest changes to true while a request is in flight
  useEffect(() => {
    if (suppressRequest && loadingRequestIdRef.current !== null) {
      loadingRequestIdRef.current = null
      onLoading(false)
    }
  }, [suppressRequest])

  // REMOVED: no longer mirror `data` → onData here
  // useEffect(() => {
  //   onData(data ?? { success: false, error: null, items: [] })
  // }, [data])

  const refetch = useCallback(
    (
      overrideParams: Partial<UseMediaLibrarySearchProps<T>> = {},
    ): Promise<void> => runRequest(overrideParams),
    [runRequest],
  )

  return { refetch, loading, abort }
}

export default useMediaLibrarySearch
